import { ElForm, ElFormItem } from 'element-plus'
import { useI18n } from 'i18n-utils'
import { CommonConfirmSecurityAuthType } from 'network/src/constants/security'
import { useUserInfoStore } from 'store'
import { DefineComponentProps } from 'utils'
import { CustomButton } from '../components/Button'
import { useSecuritySettingPassword } from '../stores/use-security-setting-password'
import { DialogHeader, HocElDialog, InputView, PasswordAuthSource, SecurityBottom, UiUtils } from '..'
import './TradePasswordModal.scss'

export enum TradePasswordScene {
  /** 交易 */
  Trade = 'trade',
  /** 设置支付密码 */
  SetPassword = 'setPassword',
}

export type TradePasswordModalProps = {
  onResolve?: () => void | Promise<void>
  onReject?: () => void | Promise<void>
  onClose?: () => void
  onMounted?: () => void | Promise<void>
  /** 场景 */
  scene?: TradePasswordScene
}

export const TradePasswordModal = defineComponent<TradePasswordModalProps>({
  props: DefineComponentProps<TradePasswordModalProps>(['onResolve', 'onReject', 'onClose', 'onMounted', 'scene']),
  setup(props) {
    const { t } = useI18n()
    const securitySettingPassword = useSecuritySettingPassword(PasswordAuthSource.Trade)
    const userInfoStore = useUserInfoStore()

    const onUpdateTradePassword = async () => {
      if (!userInfoStore.isOpenSecurity) {
        UiUtils.showError(t('请先绑定谷歌、手机或者邮箱'))
        return
      }
      if (userInfoStore.isSetTradePassword) {
        const res = await UiUtils.showSecurityAuth({
          type: CommonConfirmSecurityAuthType.ResetPayPassword,
          authSource: PasswordAuthSource.Trade,
        })
        securitySettingPassword.onSecurityAuthSuccess(res)
      } else {
        securitySettingPassword.onOpen()
      }
    }

    const headerName = computed(() => {
      if (userInfoStore.isSetTradePassword) return '重置支付密码'
      return '设置支付密码'
    })

    const labelName = computed(() => {
      if (userInfoStore.isSetTradePassword) return '新支付密码'
      return '支付密码'
    })

    const confirmLabelName = computed(() => {
      if (userInfoStore.isSetTradePassword) return '确认新支付密码'
      return '确认支付密码'
    })

    const disabled = computed(() => {
      if (userInfoStore.isSetTradePassword) {
        if (securitySettingPassword.formData.value.originTradePwd?.length !== 6) return true
      }
      if (securitySettingPassword.formData.value.tradePwd?.length !== 6) return true
      if (securitySettingPassword.formData.value.confirmTradePwd !== securitySettingPassword.formData.value.tradePwd) return true
      return false
    })

    onBeforeMount(async () => {
      onUpdateTradePassword()
    })

    const onSubmit = async () => {
      const res = await securitySettingPassword.onSubmit()
      if (res) {
        props.onResolve?.()
        setTimeout(() => {
          props.onClose?.()
        }, 300)
      }
    }

    onMounted(async () => {
      nextTick(async () => {
        await props.onMounted?.()
      })
    })

    return () => (
      <div class="trade-password-layout">
        <HocElDialog v-model={securitySettingPassword.visible.value} width={430} offsetTop={64} closeOnClickModal={false} showClose={false}>
          {{
            header: () => {
              return <DialogHeader onClose={securitySettingPassword.onClose}>{t(headerName.value)}</DialogHeader>
            },
            default: () => (
              <div class="bind-content">
                <ElForm ref={securitySettingPassword.formInstance} model={securitySettingPassword.formData.value} labelPosition="top">
                  {userInfoStore.isSetTradePassword && (
                    <ElFormItem label={t('当前支付密码')} prop="originTradePwd" rules={securitySettingPassword.rules.value.originTradePwd}>
                      <InputView v-model={securitySettingPassword.formData.value.originTradePwd} placeholder={t('请输入当前支付密码')} type="password" isNumber maxlength={6} />
                    </ElFormItem>
                  )}
                  <ElFormItem label={t(labelName.value)} prop="tradePwd" rules={securitySettingPassword.rules.value.tradePwd}>
                    <InputView v-model={securitySettingPassword.formData.value.tradePwd} placeholder={t('请输入支付密码,6位数字')} type="password" isNumber maxlength={6} />
                  </ElFormItem>
                  <ElFormItem label={t(confirmLabelName.value)} prop="confirmTradePwd" rules={securitySettingPassword.rules.value.confirmTradePwd}>
                    <InputView v-model={securitySettingPassword.formData.value.confirmTradePwd} placeholder={t('请确认支付密码')} type="password" isNumber maxlength={6} />
                  </ElFormItem>
                </ElForm>
                <CustomButton class={'next-btn'} type={'primary'} loading={securitySettingPassword.loading.value} disabled={disabled.value} onClick={onSubmit}>
                  {t('确定')}
                </CustomButton>
                <SecurityBottom />
              </div>
            ),
          }}
        </HocElDialog>
      </div>
    )
  },
})
